<template>
  <div v-if="isLoading" class="flex justify-center items-center min-h-[400px]">
    <spinner-view />
  </div>
  <ErrorState
    v-else-if="fetchError && !allData.length"
    title="Couldn't load inventory"
    :message="fetchError"
    @retry="getInventoryData"
  />
  <div v-else class="inventory-page space-y-5">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">Inventory</h1>
        <p class="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
          {{ allData.length }} item{{ allData.length !== 1 ? 's' : '' }} across {{ supplierCount }} supplier{{ supplierCount !== 1 ? 's' : '' }}
        </p>
      </div>
      <button
        @click="exportInventoryToCSV"
        :disabled="isExporting"
        class="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-primary-700 text-white hover:bg-primary-800 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span class="material-icons text-[18px]" aria-hidden="true">{{ isExporting ? 'hourglass_top' : 'download' }}</span>
        {{ isExporting ? 'Exporting...' : 'Export CSV' }}
      </button>
    </div>

    <AlertBanner v-if="fetchError" type="error" message="Couldn't refresh inventory — showing the last loaded list." :dismissible="false" class="mb-4" />

    <!-- Stats Cards -->
    <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" v-if="containerTotals">
      <div
        v-for="(count, containerType) in containerTotals"
        :key="containerType"
        class="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-4"
      >
        <p class="text-[11px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">
          {{ containerType }}
        </p>
        <p class="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tabular-nums">{{ count }}</p>
      </div>
    </div>

    <!-- Filter Bar -->
    <div class="flex items-center gap-4 px-4 py-3 bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 shadow-soft">
      <div class="flex flex-wrap items-center gap-3 flex-1">
        <!-- Search -->
        <div class="relative flex-1 min-w-[220px] max-w-sm">
          <span class="material-icons absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500 text-lg pointer-events-none" aria-hidden="true">search</span>
          <input
            v-model="searchValue"
            type="text"
            :placeholder="searchPlaceholder"
            aria-label="Search inventory"
            class="w-full py-2 pl-10 pr-4 bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-600 rounded-lg text-sm text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 dark:placeholder-neutral-500 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400"
          />
        </div>

        <!-- Field Selector -->
        <select
          v-model="searchField"
          aria-label="Search field"
          class="py-2 px-3 bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-600 rounded-lg text-sm text-neutral-700 dark:text-neutral-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400 cursor-pointer min-w-[160px]"
        >
          <option value="SupplierName">Supplier Name</option>
          <option value="LocationName">Location Name</option>
          <option value="ContainerType">Container Type</option>
        </select>

        <!-- Clear -->
        <button
          v-if="searchValue"
          @click="searchValue = ''"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
        >
          <span class="material-icons text-sm" aria-hidden="true">close</span>
          Clear
        </button>
      </div>
    </div>

    <!-- Data Table -->
    <DataTable
      :data="filteredData"
      :columns="tableColumns"
      :loading="isLoading"
      :searchable="false"
      :show-index="true"
      :pagination="true"
      loading-text="Loading inventory..."
      empty-text="No inventory items found"
    >
      <template #cell-SupplierName="{ row }">
        <router-link
          :to="{ name: 'SupplierView', params: { id: row.SupplierID } }"
          class="inline-flex items-center gap-2 group/link"
        >
          <div class="w-7 h-7 rounded-lg bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center flex-shrink-0">
            <span class="text-xs font-bold text-primary-700 dark:text-primary-300">
              {{ row.SupplierName?.charAt(0)?.toUpperCase() }}
            </span>
          </div>
          <span class="text-sm font-medium text-neutral-900 dark:text-neutral-100 group-hover/link:text-primary-600 dark:group-hover/link:text-primary-400 transition-colors">
            {{ row.SupplierName }}
          </span>
        </router-link>
      </template>

      <template #cell-LocationName="{ row }">
        <router-link
          v-if="row.LocationName && row.LocationID"
          :to="{
            name: 'LocationView',
            params: {
              locationId: row.LocationID,
              supplierId: row.SupplierID,
            },
          }"
          class="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors"
        >
          {{ row.LocationName }}
        </router-link>
        <span v-else class="text-neutral-300 dark:text-neutral-600">&mdash;</span>
      </template>

      <template #cell-ContainerType="{ row }">
        <span class="text-xs font-mono font-medium px-2 py-0.5 rounded bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300">
          {{ row.ContainerType }}
        </span>
      </template>

      <template #cell-NoOfContainers="{ row }">
        <span class="text-sm font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">{{ row.NoOfContainers }}</span>
      </template>
    </DataTable>
  </div>
</template>

<script>
import SpinnerView from "@/components/SpinnerView.vue";
import DataTable from "@/components/ui/DataTable.vue";
import ErrorState from "@/components/ui/ErrorState.vue";
import AlertBanner from "@/components/ui/AlertBanner.vue";
import useToast from "@/composables/useToast";
import { ref, computed } from "vue";
import { useStore } from "vuex";

export default {
  components: { SpinnerView, DataTable, ErrorState, AlertBanner },
  setup() {
    const store = useStore();
    store.commit("setCurrentRoute", "Inventory");
    const { showToast } = useToast();

    const allData = ref([]);
    const totals_data = ref({});
    const isLoading = ref(true);
    const fetchError = ref(null);
    const searchField = ref("SupplierName");
    const searchValue = ref("");
    const isExporting = ref(false);

    const tableColumns = [
      { key: "SupplierName", label: "Supplier", sortable: true },
      { key: "LocationName", label: "Location", sortable: true, align: "center" },
      { key: "ContainerType", label: "Container", sortable: true, align: "center" },
      { key: "NoOfContainers", label: "Qty", sortable: true, align: "center" },
    ];

    const containerTotals = computed(() => totals_data.value?.container_totals || null);

    const supplierCount = computed(() => {
      const names = new Set(allData.value.map(d => d.SupplierName));
      return names.size;
    });

    const searchPlaceholder = computed(() => {
      const map = {
        SupplierName: "Search suppliers...",
        LocationName: "Search locations...",
        ContainerType: "Search container types...",
      };
      return map[searchField.value] || "Search...";
    });

    // Computed filter - never mutates source data
    const filteredData = computed(() => {
      if (!searchValue.value) return allData.value;
      const q = searchValue.value.toLowerCase();
      return allData.value.filter(item => {
        const val = item[searchField.value];
        return val && String(val).toLowerCase().includes(q);
      });
    });

    const getInventoryData = async () => {
      isLoading.value = true;
      try {
        const response = await fetch(
          `${store.state.apiUrl}/getInventoryListBySite/${store.state.currentSite}`
        );
        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }
        const result = await response.json();
        allData.value = result.container_data || [];
        totals_data.value = result.totals_data || {};
        fetchError.value = null;
      } catch (error) {
        console.error("Error fetching inventory:", error);
        fetchError.value = "Failed to load inventory. Please try again.";
      } finally {
        isLoading.value = false;
      }
    };

    getInventoryData();

    const exportInventoryToCSV = () => {
      try {
        isExporting.value = true;
        // Quote every field so names containing commas/quotes don't shift columns
        const esc = (v) => '"' + String(v ?? "").replace(/"/g, '""') + '"';
        const headers = ["Supplier Name", "Location Name", "Container Type", "No of Containers"];
        const rows = allData.value.map(item =>
          [item.SupplierName, item.LocationName, item.ContainerType, item.NoOfContainers].map(esc).join(",")
        );
        const csv = [headers.map(esc).join(","), ...rows].join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "inventory.csv";
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Export error:", error);
        showToast("error", "Failed to export inventory. Please try again.");
      } finally {
        isExporting.value = false;
      }
    };

    return {
      isLoading,
      fetchError,
      getInventoryData,
      allData,
      filteredData,
      containerTotals,
      supplierCount,
      tableColumns,
      searchField,
      searchValue,
      searchPlaceholder,
      exportInventoryToCSV,
      isExporting,
    };
  },
};
</script>

<style scoped>
@media (prefers-reduced-motion: no-preference) {
  .inventory-page > * {
    animation: fadeSlideIn 0.35s cubic-bezier(0.4, 0, 0.2, 1) both;
  }
  .inventory-page > *:nth-child(1) { animation-delay: 0s; }
  .inventory-page > *:nth-child(2) { animation-delay: 0.05s; }
  .inventory-page > *:nth-child(3) { animation-delay: 0.1s; }
  .inventory-page > *:nth-child(4) { animation-delay: 0.15s; }
}

@keyframes fadeSlideIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
